import { useEffect, useRef } from 'react';

// Selector for keyboard-focusable elements. `[tabindex="-1"]` is excluded
// because it's specifically opt-out of tab order; everything else with a
// non-negative tabindex is included.
const FOCUSABLE = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Centralised modal a11y wiring shared by every portalised dialog:
//   1. ESC closes the modal
//   2. Body scroll is locked while the modal is open (also fixes iOS
//      Safari rubber-banding behind a fixed overlay)
//   3. Focus moves into the dialog on mount, is trapped on Tab/Shift+Tab,
//      and is restored to whatever was focused before the modal opened.
//
// Returns a ref to attach to the dialog container (the inner element
// that holds the dialog's content — NOT the full-viewport backdrop).
// The container's focusable descendants form the trap boundary.
//
// `onClose` is captured in a ref so the effect doesn't re-run on every
// render that produces a new arrow-function identity.
export const useModalA11y = (onClose, isOpen = true) => {
  const containerRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !containerRef.current) return;
      const focusable = Array.from(
        containerRef.current.querySelectorAll(FOCUSABLE),
      ).filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKey);

    // Defer initial focus by a frame so the dialog tree is in the DOM.
    // Prefers an element marked `data-autofocus`, falls back to the
    // first focusable descendant.
    const focusFrame = requestAnimationFrame(() => {
      const node = containerRef.current;
      if (!node) return;
      const target = node.querySelector('[data-autofocus]')
        || node.querySelector(FOCUSABLE);
      if (target instanceof HTMLElement) target.focus();
    });

    return () => {
      window.removeEventListener('keydown', handleKey);
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  return containerRef;
};
